import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiPut, apiDelete, t } from "../api_helper"
import { createCategoryOffline } from "../lib/offline_api"
import { icon } from "../lib/icons"
import { networkStatus } from "../lib/network_status"
import { isOfflineError, showOfflinePlaceholder, alertOffline } from "../lib/offline_ui"

export default class extends Controller {
  static targets = ["list", "name", "icon", "color", "kind", "expenseBtn", "incomeBtn", "addBtn", "formContainer", "formTitle", "submitBtn"]

  connect() {
    this.selectedKind = "expense"
    this.editingCategoryId = null
    this.load()
  }

  async load() {
    try {
      const data = await apiGet("/api/categories")
      const categories = data.categories || data
      this.renderList(categories)
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasListTarget ? this.listTarget : null, () => this.load()); return }
      console.error("Categories load error:", e)
    }
  }

  sortCategories(categories) {
    return categories.slice().sort((a, b) => {
      if (a.system === b.system) return a.name.localeCompare(b.name)
      return a.system ? 1 : -1
    })
  }

  renderList(categories) {
    if (!this.hasListTarget) return

    const expenses = this.sortCategories(categories.filter(c => c.kind === "expense"))
    const incomes = this.sortCategories(categories.filter(c => c.kind === "income"))

    this.listTarget.innerHTML = `<div>
      <div style="font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: rgb(148,163,184); padding: 0.5rem 0; margin-top: 0.25rem;">${t("categories.expenses")}</div>
      <div style="padding: 0 1rem; border-radius: 0.75rem; background-color: rgb(248,250,252); margin-bottom: 1rem;">
        ${expenses.length
          ? expenses.map((c, i) => this.categoryRow(c, i < expenses.length - 1)).join("")
          : `<div style="text-align: center; padding: 1rem; color: rgb(148,163,184); font-size: 0.875rem;">${t("dashboard.no_transactions")}</div>`
        }
      </div>
      <div style="font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: rgb(148,163,184); padding: 0.5rem 0;">${t("categories.incomes")}</div>
      <div style="padding: 0 1rem; border-radius: 0.75rem; background-color: rgb(248,250,252);">
        ${incomes.length
          ? incomes.map((c, i) => this.categoryRow(c, i < incomes.length - 1)).join("")
          : `<div style="text-align: center; padding: 1rem; color: rgb(148,163,184); font-size: 0.875rem;">${t("dashboard.no_transactions")}</div>`
        }
      </div>
    </div>`
  }

  categoryRow(c, showBorder) {
    const isCustom = !c.system
    const editData = isCustom ? `data-action="click->pwa-categories#editCategory"
                data-category-id="${c.id}" data-category-name="${this.escapeHtml(c.name)}"
                data-category-icon="${this.escapeHtml(c.icon)}" data-category-color="${c.color}" data-category-kind="${c.kind}"` : ""

    return `
      <div style="display: flex; align-items: center; justify-content: space-between; padding: 0.625rem 0; ${showBorder ? 'border-bottom: 1px solid #e2e8f0;' : ''} ${isCustom ? 'cursor: pointer;' : ''}"
           ${editData}>
        <div style="display: flex; align-items: center; gap: 0.75rem; flex: 1; min-width: 0;">
          <div style="width: 40px; height: 40px; border-radius: 12px; background-color: ${c.color}15; font-size: 20px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
            ${c.icon}
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem; min-width: 0;">
            <span style="font-size: 0.875rem; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${c.name}</span>
            ${c.system ? `<span style="font-size: 0.75rem; flex-shrink: 0; padding: 0.125rem 0.375rem; border-radius: 9999px; background-color: rgba(148,163,184,0.15); color: rgb(148,163,184);">${t("categories.system")}</span>` : ""}
          </div>
        </div>
        ${isCustom ? `<button data-action="click->pwa-categories#deleteCategory" data-category-id="${c.id}"
                style="flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; color: rgb(148,163,184); background: none; border: none; cursor: pointer;"
                onclick="event.stopPropagation()">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 4h12M5.33 4V2.67a1.33 1.33 0 011.34-1.34h2.66a1.33 1.33 0 011.34 1.34V4M6.67 7.33v4M9.33 7.33v4M12.67 4v9.33a1.33 1.33 0 01-1.34 1.34H4.67a1.33 1.33 0 01-1.34-1.34V4"/>
          </svg>
        </button>` : ""}
      </div>`
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  showForm() {
    this.editingCategoryId = null
    this.nameTarget.value = ""
    this.iconTarget.value = ""
    this.colorTarget.value = "#6C5CE7"
    this.selectedKind = "expense"
    if (this.hasKindTarget) this.kindTarget.value = "expense"
    this.updateKindUI()
    this.formTitleTarget.textContent = t("categories.new_category")
    this.submitBtnTarget.textContent = t("categories.add")
    this.formContainerTarget.classList.remove("hidden")
    setTimeout(() => this.nameTarget.focus(), 100)
  }

  hideForm() {
    this.formContainerTarget.classList.add("hidden")
    this.editingCategoryId = null
  }

  editCategory(event) {
    if (event.target.closest("[data-action*='deleteCategory']")) return
    const el = event.currentTarget
    this.editingCategoryId = el.dataset.categoryId
    this.nameTarget.value = el.dataset.categoryName
    this.iconTarget.value = el.dataset.categoryIcon
    this.colorTarget.value = el.dataset.categoryColor
    this.selectedKind = el.dataset.categoryKind
    if (this.hasKindTarget) this.kindTarget.value = this.selectedKind
    this.updateKindUI()
    this.formTitleTarget.textContent = t("categories.edit_category")
    this.submitBtnTarget.textContent = t("categories.save")
    this.formContainerTarget.classList.remove("hidden")
    setTimeout(() => this.nameTarget.focus(), 100)
  }

  setExpense() {
    this.selectedKind = "expense"
    if (this.hasKindTarget) this.kindTarget.value = "expense"
    this.updateKindUI()
  }

  setIncome() {
    this.selectedKind = "income"
    if (this.hasKindTarget) this.kindTarget.value = "income"
    this.updateKindUI()
  }

  updateKindUI() {
    if (this.hasExpenseBtnTarget && this.hasIncomeBtnTarget) {
      if (this.selectedKind === "expense") {
        this.expenseBtnTarget.style.backgroundColor = "rgb(239,68,68)"
        this.expenseBtnTarget.style.color = "#fff"
        this.expenseBtnTarget.style.border = "none"
        this.incomeBtnTarget.style.backgroundColor = "transparent"
        this.incomeBtnTarget.style.color = "rgb(148,163,184)"
        this.incomeBtnTarget.style.border = "1px solid rgba(148,163,184,0.19)"
      } else {
        this.incomeBtnTarget.style.backgroundColor = "rgb(16,185,129)"
        this.incomeBtnTarget.style.color = "#fff"
        this.incomeBtnTarget.style.border = "none"
        this.expenseBtnTarget.style.backgroundColor = "transparent"
        this.expenseBtnTarget.style.color = "rgb(148,163,184)"
        this.expenseBtnTarget.style.border = "1px solid rgba(148,163,184,0.19)"
      }
    }
  }

  async submit(event) {
    event.preventDefault()
    const data = {
      category: {
        name: this.nameTarget.value,
        icon: this.iconTarget.value || "📌",
        color: this.colorTarget.value || "#6C5CE7",
        kind: this.selectedKind
      }
    }

    try {
      if (this.editingCategoryId) {
        await apiPut(`/api/categories/${this.editingCategoryId}`, data)
      } else {
        await createCategoryOffline(data.category)
      }
      this.hideForm()
      this.load()
    } catch (e) {
      console.error("Category save error:", e)
      alert(e.message || "Error saving category")
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async deleteCategory(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const id = event.currentTarget.dataset.categoryId
    if (!confirm(t("categories.delete_confirm") || "Delete this category?")) return
    try {
      await apiDelete(`/api/categories/${id}`)
      this.load()
    } catch (e) {
      alert(t("categories.delete_error") || "Cannot delete this category")
    }
  }
}
